import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router-dom";
import { bindActionCreators } from "redux";
import { InvoicePreference } from "../../../types/invoice-preference/invoice-preference.type";
import { PaymentPreference } from "../../../types/payment-preference/payment-preference.type";
import { TabBody } from "../../../_components/atoms/tab-body.component";
import { TabPills } from "../../../_components/atoms/tab-pills.component";
import Label from "../../../_components/typography/label.typography";
import { history } from "../../../_config";
import {
  getInvoicePreference,
  getPaymentPreference,
  updateInvoicePreference,
  updatePaymentPreference,
} from "../../../_store/preference/preference.action";
import { PreferenceType } from "../../../_store/preference/preference.reducer";
import { setCommunity } from "../../../_store/_actions/community.actions";
import CommunityInvoicePage from "./community-invoicing-setting/community-invoice.page";
import CommunityPaymentsPage from "./community-payments-setting/payments";

export const COMMUNITY_PAYWALL_SETTINGS_PAGE_ROUTE =
  "/settings/PAYWALL/:tabPath";

const Tabs = Object.freeze({
  paywall: "payment",
  invoice: "Invoice",
});

export const getPaywallPageRoute = (tabPath: keyof typeof Tabs) =>
  `/settings/paywall/${tabPath ?? Tabs.paywall}`;

interface PaywallProps {
  community: any;
  user: any;
  communityPac: any;
  preference: PreferenceType;
  /**
   * Get community payment gateway details from backend and store in redux
   */
  getPaymentPreference: () => void;
  /**
   * Get community invoice details from backend and store in redux
   */
  getInvoicePreference: () => void;
  /**
   * Update payment preference on backend and store in redux
   */
  updatePaymentPreference: (
    payload: any,
    onSuccess: (_: PaymentPreference) => void,
    onFailed: (_: any) => void
  ) => void;
  /**
   * Update invoice preference on backend and store in redux
   */
  updateInvoicePreference: (
    payload: any,
    onSuccess: (_: InvoicePreference) => void,
    onFailed: (_: any) => void
  ) => void;
  /**
   * Save updated community details in redux
   */
  onCommunityUpdate: (community: any) => void;
}
/**
 * Paywall page to manage community payment and invoice settings
 */
function PaywallPageComponent({
  user,
  community,
  communityPac,
  onCommunityUpdate,
  updatePaymentPreference,
  updateInvoicePreference,
  getPaymentPreference,
  getInvoicePreference,
  preference,
}: PaywallProps) {
  const { tabPath } = useParams() as { tabPath: keyof typeof Tabs };

  const [selectedTab, setSelectedTab] = useState<keyof typeof Tabs>(tabPath);

  // Get community payment gateway details and store in redux
  useEffect(() => {
    if ((community?.id, preference.paymentPreference.status === "idle")) {
      getPaymentPreference();
    }
    if ((community?.id, preference.invoicePreference.status === "idle")) {
      getInvoicePreference();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [community?.id, preference]);

  return (
    <div className="UserWallPage flex rounded pensil-card shadow">
      <div className="main-content theme-bg-surface">
        {/* header card */}
        <div className="flex flex-col gap-4 border theme-border-default rounded pt-4 px-4">
          <Label variant="t1" size="body1">
            Paywall
          </Label>
          <TabPills
            className="px-0"
            selectedTab={selectedTab}
            setSelectedTab={(tabPath: keyof typeof Tabs) => {
              history.push(getPaywallPageRoute(tabPath));
              setSelectedTab(tabPath);
            }}
            tabs={[
              {
                label: "Payment",
                key: Tabs.paywall,
              },
              {
                label: "Invoice",
                key: Tabs.invoice,
              },
            ]}
          />
        </div>

        <TabBody
          selectedTab={selectedTab as any}
          components={{
            [Tabs.paywall]: (
              <CommunityPaymentsPage
                community={community}
                user={user}
                paymentPreference={preference.paymentPreference}
                onPaymentPreferenceUpdate={updatePaymentPreference}
                onCommunityUpdate={onCommunityUpdate}
              />
            ),
            [Tabs.invoice]: (
              <CommunityInvoicePage
                user={user}
                invoicePreference={preference.invoicePreference}
                updateInvoicePreference={updateInvoicePreference}
              />
            ),
          }}
        />
      </div>
    </div>
  );
}

const stp = (state: any) => ({
  user: state.auth,
  community: state.community,
  communityPac: state.communityPac,
  preference: state.preferenceReducer,
});

const dtp = (dispatch: any) =>
  bindActionCreators(
    {
      onCommunityUpdate: setCommunity,
      updatePaymentPreference: updatePaymentPreference,
      updateInvoicePreference: updateInvoicePreference,
      getPaymentPreference: getPaymentPreference,
      getInvoicePreference: getInvoicePreference,
    },
    dispatch
  );

const PaywallPage = connect(stp, dtp)(PaywallPageComponent);

export default PaywallPage;
